import axios from 'axios';
import { CONFIG } from '../config/constants';
import { UserRole } from '../config/roles';

const API_BASE_URL = CONFIG.API.API_URL;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface UserListItem {
  id: number;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  data: {
    data: UserListItem[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  roles: Record<string, string>;
  success: boolean;
}

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  role?: UserRole;
}

export interface UserFilters {
  search?: string;
  role?: string;
  per_page?: number;
}

const userManagementService = {
  // Get users list with pagination and filters
  getUsers: async (page: number = 1, filters?: UserFilters): Promise<UserListResponse> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        ...(filters?.search && { search: filters.search }),
        ...(filters?.role && { role: filters.role }),
        ...(filters?.per_page && { per_page: filters.per_page.toString() }),
      });

      const response = await apiClient.get(`/users?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy danh sách người dùng');
    }
  },

  // Create new user
  createUser: async (userData: CreateUserData): Promise<{ success: boolean; message: string; data: UserListItem }> => {
    try {
      const response = await apiClient.post('/users', userData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể tạo người dùng');
    }
  },

  // Get single user
  getUser: async (userId: number): Promise<{ success: boolean; data: UserListItem }> => {
    try {
      const response = await apiClient.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy thông tin người dùng');
    }
  },

  // Update user
  updateUser: async (userId: number, userData: UpdateUserData): Promise<{ success: boolean; message: string; data: UserListItem }> => {
    try {
      const response = await apiClient.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể cập nhật người dùng');
    }
  },

  // Delete user
  deleteUser: async (userId: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xóa người dùng');
    }
  },

  // Update user role
  updateUserRole: async (userId: number, role: string): Promise<{ success: boolean; message: string; data: UserListItem }> => {
    try {
      const response = await apiClient.put(`/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể cập nhật vai trò người dùng');
    }
  },

  // Get available roles
  getRoles: async (): Promise<{ success: boolean; data: Record<string, string> }> => {
    try {
      const response = await apiClient.get('/roles');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy danh sách vai trò');
    }
  },
};

export default userManagementService; 